/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { InView } from 'react-intersection-observer';

import {
  Page,
  Content,
  PageWithNav
} from '../../../../theme/page';
import {
  getPageParams
} from '../../../api';

import dynamicModules from '../../../../tissues/dynamicModules';
import MoreStories from '../../../../tissues/more-stories';
import InnerPageNavigation from '../../../../tissues/inner-page-navigation';
import formatCatSubcatFromHM from '../../../../organisms/algolia-cms/utils/formatCatSubcatFromHM/index';
import LeScoopBreadCrumbs from '../../../../organisms/algolia-cms/le_scoop-breadcrumbs';
import { articleGSSP } from '../../../../organisms/algolia-cms/gssp/index';

import isFullWidthBelowMedium from '../../../../utils/isFullWidthBelowMedium';
import isFullWidthMobileOnly from '../../../../utils/isFullWidthMobileOnly';
import dateToString from '../../../../utils/dateToString';
import getCmsCanonicalUrl from '../../../../utils/getCmsCanonicalUrl';
import hasHr from '../../../../utils/hasHr';
import { generateMeta, getMetaImage } from '../../../../utils/meta';
import isModuleBackgroundWhite from '../../../../utils/isModuleBackgroundWhite';
import { logAmplitude } from '../../../../utils/amplitude';
import deconstructPath from '../../../../organisms/algolia-plp/utils/deconstructPath';
import getModuleTemplates from '../../../../utils/cms/getModuleTemplates';

const isCircleBanner = (modulename) => modulename === 'circle_banner';
const isHeading = (modulename) => modulename === 'heading';
const isDoubleBanner = (modulename) => modulename === 'double_banner';
const isAccordion = (modulename) => modulename === 'accordion';
const isSocialShare = (modulename) => modulename === 'social_share';
const isBlockQuote = (modulename) => modulename === 'block_quote';

const Loading = styled.span`
  ${(props) => props.theme.loader()}
`;

const IntersectionObserverElement = styled.div`
  height: 0px;
  width: 100%;
`;

const Story = (props) => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [moreStories, setMoreStories] = useState({});
  const needsSocialShare = props.modules.findIndex((element) => element.__component === 'modules.social_share') === -1;
  const hasHero = props.modules.findIndex((element) => element.__component === 'modules.hero');
  const { isFallback } = useRouter();

  useEffect(() => {
    // grab previously visited page using history, default to current page
    // for direct visit
    let parentPage = router.asPath;

    if (props.history?.length > 0) {
      const { length } = props.history;
      parentPage = props.history[length - 1];
    }

    logAmplitude(null, {
      parentPage,
      category: props.category?.displayName,
      subcategory: props.subcategory?.displayName
    });
  }, [props.category, props.history, props.subcategory, router.asPath]);

  const addSocialShare = (modules) => {
    // adds a social_share module to stories that don't have one
    // the logic adds it under the first hero element
    // create initial templates
    const newTemplates = getModuleTemplates(modules);
    // create the social_share data
    const socialData = {
      Template: dynamicModules.social_share,
      position: newTemplates[hasHero].position + 0.5,
      title: 'social_share',
      data: {
        // social_share module attributes
        email: true,
        twitter: true,
        facebook: true,
        pinterest: true
      }
    };
    newTemplates.splice(hasHero + 1, 0, socialData);
    return newTemplates;
  };

  const templates = needsSocialShare && hasHero >= 0
    ? addSocialShare(props.modules)
    : getModuleTemplates(props.modules);

  const needsMoreStories = templates.findIndex((element) => element.__component === 'modules.more_stories') === -1;

  const renderMoreStories = async () => {
    if (needsMoreStories) {
      const date = Date.now();
      // make a call to the strapi endpoint to get the data inside the content
      const moreStoriesRes = await getPageParams({
        params: {
          'filters[type][$eq]': 'story',
          publicationState: 'live',
          'filters[in_le_scoop_module][$eq]': true,
          'filters[publish_date][$lte]': date,
          sort: 'publish_date:desc',
          'pagination[pageSize]': 4,
          'filters[url][$ne]': props.page.url
        }
      });

      if (moreStoriesRes?.data?.length > 0) {
        const moreStoriesData = moreStoriesRes.data.reduce((acc, cur, idx) => {
          const dataFormat = {
            url: cur?.url,
            image: cur?.social_image,
            heading: cur?.title,
            image_alt: cur?.meta_data?.description ?? cur?.title
          };
          const retVal = {
            more_stories: {
              ...acc.more_stories,
              [idx]: dataFormat
            }
          };
          return retVal;
        }, { more_stories: {} });
        setMoreStories(moreStoriesData);
        setLoading(false);
      }
    }
    setLoading(false);
  };

  const handleOnIntersectionChange = (inView) => {
    if (!inView) return;
    renderMoreStories();
  };

  if (isFallback) {
    return (
      <Page background="default">
        <Loading />
      </Page>
    );
  }

  return (
    <>
      <Head>
        {
          Object.entries(props.page).length > 0 && generateMeta({
            key: 'cms',
            title: props.page.meta_data?.title,
            description: props.page.meta_data?.description,
            og: {
              description: props.page.meta_data?.description,
              image: getMetaImage(props),
              title: props.page.meta_data?.title,
              url: `${process.env.NEXT_PUBLIC_CLIENT_HOST}${router.asPath}`
            },
            twitter: {
              description: props.page.meta_data?.description,
              image: getMetaImage(props),
              title: props.page.meta_data?.title
            }
          })
        }

        <link key="canonical" rel="canonical" href={getCmsCanonicalUrl(router)} />

        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "${process.env.NEXT_PUBLIC_CLIENT_HOST}${props.page.url}"
              },
              "headline": "${props.page.meta_data?.title}",
              "description": "${props.page.meta_data?.description}",
              "image": "${getMetaImage(props)}",
              "author": {
                "@type": "Organization",
                "name": "Maisonette"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Maisonette",
                "logo": {
                  "@type": "ImageObject",
                  "url": "${getMetaImage()}"
                }
              },
              "datePublished": "${dateToString(props.page.publish_date, 'YYYY-MM-DD')}",
              "dateModified": "${dateToString(props.page.updatedAt, 'YYYY-MM-DD')}"
            }`
          }}
        />

      </Head>
      {props?.page?.inner_page_navigation ? (
        <Page background={props.page.background} id="maincontent">
          {/* // TODO: can we remove this? will a story ever have inner page nav? */}
          <LeScoopBreadCrumbs
            category={props.category}
            subcategory={props.subcategory}
            path={router.asPath}
            isStory
          />
          <PageWithNav>
            <InnerPageNavigation activePageName={props.page.url} />
            {templates.map((template, index) => {
              const {
                Template,
                title,
                position,
                data
              } = template;
              return data && Object.keys(data).length > 0 ? (
                <Content
                  className="cms-content"
                  template={template}
                  isCMS
                  hasHr={hasHr(data)}
                  isDoubleBanner={isDoubleBanner(title)}
                  noPadding={title === 'markup'}
                  key={`${props.page.id}-${title}-${position}`}
                  isCircleBanner={isCircleBanner(title)}
                  isWhite={isModuleBackgroundWhite(title)}
                  isHeading={isHeading(title)}
                  isAccordion={isAccordion(title)}
                  isSocialShare={isSocialShare(title)}
                  isFullWidthBelowMedium={isFullWidthBelowMedium(title)}
                  isFullWidthMobileOnly={isFullWidthMobileOnly(title)}
                >
                  <Template
                    data={data}
                    isStory
                    disableLazyload={index < 2}
                  />
                </Content>
              ) : null;
            })}
            {(needsMoreStories && !loading) && (
              <Content isCMS>
                <MoreStories data={moreStories} />
              </Content>
            )}
          </PageWithNav>
        </Page>
      ) : (
        <Page background={props.page.background} id="maincontent">
          <LeScoopBreadCrumbs
            category={props.category}
            subcategory={props.subcategory}
            path={router.asPath}
            isStory
          />
          {templates.map((template, index) => {
            const {
              Template,
              title,
              position,
              data
            } = template;
            return data && Object.keys(data).length > 0 ? (
              <Content
                className="cms-content"
                template={template}
                isCMS
                hasHr={hasHr(data)}
                isDoubleBanner={isDoubleBanner(title)}
                noPadding={title === 'markup'}
                key={`${props.page.id}-${title}-${position}`}
                isCircleBanner={isCircleBanner(title)}
                isWhite={isModuleBackgroundWhite(title)}
                isAccordion={isAccordion(title)}
                isSocialShare={isSocialShare(title)}
                isBlockQuote={isBlockQuote(title)}
                isNeon={props.page.background === 'neon'}
                isFullWidthBelowMedium={isFullWidthBelowMedium(title)}
                isFullWidthMobileOnly={isFullWidthMobileOnly(title)}
              >
                <Template
                  data={data}
                  isStory
                  disableLazyload={index < 2}
                />
              </Content>
            ) : null;
          })}
          <InView threshold={0} onChange={handleOnIntersectionChange}>
            {({ ref }) => <IntersectionObserverElement ref={ref} />}
          </InView>
          {(needsMoreStories && !loading) && (
            <Content isCMS>
              <MoreStories data={moreStories} />
            </Content>
          )}
        </Page>
      )}
    </>
  );
};

export const getServerSideProps = async ({ params }) => {
  let props = {};
  props = await articleGSSP({ params });

  if (props.code !== 'error' && props.page.url) {
    const { hierarchicalMenu } = deconstructPath(props.page.url);

    const { categoryData, subcategoryData } = formatCatSubcatFromHM(
      { hierarchicalMenu }
    );

    if (categoryData) props = { ...props, category: categoryData };
    if (subcategoryData) props = { ...props, subcategory: subcategoryData };
  }

  if (props.code === 'error') {
    return {
      redirect: {
        destination: '/le_scoop'
      }
    };
  }

  return {
    props: {
      ...props
    }
  };
};

Story.defaultProps = {
  modules: [],
  page: {},
  category: null,
  subcategory: null
};

Story.propTypes = {
  page: PropTypes.object,
  modules: PropTypes.array,
  history: PropTypes.array.isRequired,
  category: PropTypes.object,
  subcategory: PropTypes.object
};

export default Story;
